import { API_URL } from "./api";

// ══════════════════════════════════════════════════════
//  AVISOS DE ERRORES A LA API (POST /logs/front)
//  Cuando algo falla en el navegador de un visitante (un error de JavaScript, una promesa
//  rechazada o una pantalla que no se pudo pintar), se manda un aviso técnico a la API, que lo
//  deja en los logs de Railway («[front-error]»). Sin terceros y sin librerías.
//  - Solo en producción (en local, React ya enseña los errores).
//  - Va por fetch propio, no por la instancia de axios: un fallo al avisar no debe cerrar la
//    sesión (el interceptor del 401) ni provocar otro error.
//  - Se envía lo mínimo: tipo, mensaje, pila y la ruta SIN parámetros. Nada de la cuenta.
//  - Sin ruido: no avisa si no hay conexión, ni de extensiones del navegador, ni de errores
//    de red/carga de trozos (los provoca la conexión, no el código), ni el mismo error dos
//    veces. Tope de 5 avisos por visita. Tampoco avisa de errores de scripts de otros dominios.
// ══════════════════════════════════════════════════════

const MAX_AVISOS = 5;
const enviados = new Set();

const RUIDO = [
  /ResizeObserver loop/i, // aviso inofensivo de algunos navegadores
  /^Script error\.?$/i, // error de un script de otro dominio: no trae información
  /Loading (CSS )?chunk .* failed/i, // trozo de la web que no se pudo descargar (conexión o despliegue nuevo)
  /ChunkLoadError/i,
  /dynamically imported module/i,
  /Network ?Error|Failed to fetch|Load failed|NetworkError/i, // se cayó la conexión
  /AbortError|canceled|cancelled/i, // petición cancelada a propósito
];

// «archivo»: de qué script viene el error (solo lo trae el evento «error»)
export const informarError = (tipo, error, archivo = "") => {
  try {
    if (process.env.NODE_ENV !== "production" || typeof window === "undefined") return;
    if (navigator.onLine === false) return;
    // Un error de un script que no es de esta web (analítica, extensiones, anuncios) no es nuestro
    if (archivo && (!archivo.startsWith(window.location.origin) || archivo.includes("/_vercel/"))) return;
    const mensaje = String(error?.message ?? error ?? "").slice(0, 500);
    const pila = String(error?.stack ?? "").slice(0, 2000);
    if (!mensaje || RUIDO.some((re) => re.test(mensaje) || re.test(String(error?.name ?? "")))) return;
    if (/-extension:\/\/|^moz-extension|safari-web-extension/.test(pila)) return;

    const huella = `${mensaje}|${pila.slice(0, 200)}`;
    if (enviados.has(huella) || enviados.size >= MAX_AVISOS) return;
    enviados.add(huella);

    fetch(`${API_URL}/logs/front`, {
      method: "POST",
      keepalive: true, // sigue aunque la página se cierre o se recargue
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tipo, mensaje, pila, ruta: window.location.pathname }),
    }).catch(() => {
      /* si no se pudo avisar, no pasa nada */
    });
  } catch {
    /* avisar de un error nunca debe provocar otro */
  }
};

// Se llama una vez al arrancar (index.js)
export const activarAvisoDeErrores = () => {
  window.addEventListener("error", (e) => informarError("error", e.error || e.message, e.filename));
  window.addEventListener("unhandledrejection", (e) => informarError("promesa", e.reason));
};
